import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Loader2, Bot, ShieldAlert, ShieldCheck, ShieldX } from "lucide-react";

const RISK_CLASSES = {
  low:    { icon: ShieldCheck, color: "text-emerald-400", border: "border-emerald-500/50", label: "Low risk" },
  medium: { icon: ShieldAlert, color: "text-amber-400",   border: "border-amber-500/50",   label: "Medium risk" },
  high:   { icon: ShieldX,     color: "text-rose-500",    border: "border-rose-600",       label: "High risk" },
};

export default function ModerationModal({ open, onOpenChange, target }) {
  const [state, setState] = useState({ status: "idle", verdict: null, err: "" });

  const run = async () => {
    setState({ status: "loading", verdict: null, err: "" });
    try {
      const { data } = await api.post("/admin/moderate", {
        target_type: target.type,
        target_id: target.id,
      });
      setState({ status: "done", verdict: data.verdict, err: "" });
    } catch (e) {
      setState({ status: "error", verdict: null, err: e?.response?.data?.detail || "Failed" });
    }
  };

  const verdict = state.verdict;
  const risk = verdict ? RISK_CLASSES[verdict.risk_level] || RISK_CLASSES.low : null;
  const RiskIcon = risk?.icon || Bot;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass rounded-none max-w-lg border-white/10" data-testid="moderation-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Bot size={22} className="text-rose-500" />
            PandaBot review
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Claude Sonnet 4.6 analyzes the {target.type} against My Panda X community policies.
          </DialogDescription>
        </DialogHeader>

        {state.status === "idle" && (
          <div className="text-center py-6">
            <Button
              onClick={run}
              className="btn-crimson rounded-none px-6 py-6"
              data-testid="moderation-run-button"
            >
              <Bot size={16} className="mr-2" /> Analyze this {target.type}
            </Button>
          </div>
        )}

        {state.status === "loading" && (
          <div className="text-center py-10 flex flex-col items-center gap-3">
            <Loader2 size={24} className="animate-spin text-rose-500" />
            <div className="text-sm text-zinc-400">PandaBot is reading the content…</div>
          </div>
        )}

        {state.status === "error" && (
          <div className="border-l-2 border-rose-500 pl-3 py-2 text-sm text-rose-400" data-testid="moderation-error">
            {state.err}
          </div>
        )}

        {state.status === "done" && verdict && (
          <div className="space-y-4" data-testid="moderation-verdict">
            <div className={`card-surface p-4 border-l-4 ${risk.border}`}>
              <div className="flex items-center gap-2 mb-2">
                <RiskIcon size={18} className={risk.color} />
                <span className={`text-xs uppercase tracking-widest ${risk.color}`}>{risk.label}</span>
              </div>
              <div className="text-sm text-zinc-100 leading-relaxed">{verdict.explanation}</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {(verdict.categories || []).map((c) => (
                <span key={c} className="text-[10px] uppercase tracking-widest px-2 py-1 border border-white/15 text-zinc-300">
                  {c.replace(/_/g, " ")}
                </span>
              ))}
              {(verdict.categories || []).length === 0 && (
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">No violations detected</span>
              )}
            </div>
            <div className="border-l-2 border-white/10 pl-3 text-xs text-zinc-500">
              Recommendation: <span className="text-white uppercase tracking-widest">{verdict.recommendation}</span>
            </div>
            <Button
              onClick={run}
              variant="outline"
              className="w-full rounded-none bg-transparent border-white/15 text-white hover:border-rose-600"
            >
              Re-analyze
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
